import { useCallback, useEffect, useState } from "react";
import { useIPZone } from "../contexts/IPZoneContext";
import { useGeoSettings } from "../contexts/GeoSettingsContext";
import {
  Badge,
  Button,
  Checkbox,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
  message,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import {
  BellOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  SendOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { api, AlertRule, AlertForensics, NotifChannel, NotifHistoryItem, AlertMetric } from "../api";
import { useRole } from "../contexts/AuthContext";

// ── metric helpers ─────────────────────────────────────────────────────────

function fmtBps(v: number): string {
  if (v >= 1e9) return (v / 1e9).toFixed(1) + " Gbps";
  if (v >= 1e6) return (v / 1e6).toFixed(1) + " Mbps";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " kbps";
  return v + " bps";
}
function fmtPps(v: number): string {
  if (v >= 1e6) return (v / 1e6).toFixed(1) + " Mpps";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " kpps";
  return v + " pps";
}
function fmtValue(metric: string, v: number): string {
  return metric === "bps_in" || metric === "bps_out" ? fmtBps(v) : fmtPps(v);
}
function metricLabel(m: string): string {
  switch (m) {
    case "bps_in": return "Inbound bps";
    case "bps_out": return "Outbound bps";
    case "pps_in": return "Inbound pps";
    case "pps_out": return "Outbound pps";
    default: return m;
  }
}
function isBpsMetric(m: string) {
  return m === "bps_in" || m === "bps_out";
}

// ── SecondsInput ───────────────────────────────────────────────────────────
// InputNumber + "s" suffix as a proper controlled component so Form.Item
// binding works correctly (avoids the addonAfter value-undefined bug in AntD 5).

interface SecondsInputProps {
  value?: number;
  onChange?: (v: number) => void;
}
function SecondsInput({ value, onChange }: SecondsInputProps) {
  return (
    <Space.Compact style={{ width: "100%" }}>
      <InputNumber
        min={0}
        value={value}
        onChange={(v) => onChange?.(v ?? 0)}
        style={{ width: "100%" }}
      />
      <span style={{
        display: "inline-flex", alignItems: "center",
        padding: "0 11px",
        background: "var(--bg-2)", border: "1px solid var(--line)",
        borderLeft: "none", borderRadius: "0 6px 6px 0",
        color: "var(--muted)", fontSize: 13,
      }}>s</span>
    </Space.Compact>
  );
}

// ── ThresholdInput ─────────────────────────────────────────────────────────
// Lets users enter "100 Mbps" instead of raw 100000000.

const BPS_UNITS = [
  { label: "bps",  mul: 1 },
  { label: "kbps", mul: 1_000 },
  { label: "Mbps", mul: 1_000_000 },
  { label: "Gbps", mul: 1_000_000_000 },
];
const PPS_UNITS = [
  { label: "pps",  mul: 1 },
  { label: "kpps", mul: 1_000 },
  { label: "Mpps", mul: 1_000_000 },
];

function decomposeThreshold(metric: string, raw: number) {
  const units = isBpsMetric(metric) ? BPS_UNITS : PPS_UNITS;
  for (let i = units.length - 1; i >= 0; i--) {
    if (raw >= units[i].mul && raw % units[i].mul === 0) {
      return { displayVal: raw / units[i].mul, unit: units[i].label };
    }
  }
  return { displayVal: raw, unit: units[0].label };
}

interface ThresholdInputProps {
  metric: AlertMetric;
  value?: number;
  onChange?: (v: number) => void;
}
function ThresholdInput({ metric, value = 0, onChange }: ThresholdInputProps) {
  const units = isBpsMetric(metric) ? BPS_UNITS : PPS_UNITS;
  const { displayVal, unit } = decomposeThreshold(metric, value);
  const [display, setDisplay] = useState(displayVal);
  const [selUnit, setSelUnit] = useState(unit);

  // Sync only when the prop value disagrees with our locally-emitted
  // value, i.e. when an external source (form reset, editing another
  // rule, metric switch) changed it. Otherwise we'd re-decompose every
  // keystroke — and decomposeThreshold(0) returns {0, "bps"}, which
  // means typing-then-clearing the field would drop the unit dropdown
  // back to bps and never recover. Metric change always forces a resync
  // because the unit family changed (bps ↔ pps).
  useEffect(() => {
    const mul = units.find((u) => u.label === selUnit)?.mul ?? 1;
    if (Math.round(display * mul) === value) return;
    const d = decomposeThreshold(metric, value);
    setDisplay(d.displayVal);
    setSelUnit(d.unit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric, value]);

  function emit(d: number, u: string) {
    const mul = units.find((x) => x.label === u)?.mul ?? 1;
    onChange?.(Math.round(d * mul));
  }

  return (
    <Space.Compact style={{ width: "100%" }}>
      <InputNumber
        style={{ width: "65%" }}
        min={0}
        value={display}
        onChange={(v) => {
          const d = v ?? 0;
          setDisplay(d);
          emit(d, selUnit);
        }}
      />
      <Select
        style={{ width: "35%" }}
        value={selUnit}
        options={units.map((u) => ({ value: u.label, label: u.label }))}
        onChange={(u) => {
          setSelUnit(u);
          emit(display, u);
        }}
      />
    </Space.Compact>
  );
}

// ── ChannelModal ───────────────────────────────────────────────────────────

interface ChannelModalProps {
  open: boolean;
  editing: NotifChannel | null;
  cloneFrom?: NotifChannel | null; // pre-fill form from an existing channel (clone)
  onClose: () => void;
  onSaved: () => void;
}
function ChannelModal({ open, editing, cloneFrom, onClose, onSaved }: ChannelModalProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingResolve, setTestingResolve] = useState(false);
  const [chType, setChType] = useState<"telegram" | "webhook">("telegram");

  async function handleTest() {
    if (!editing) return;
    setTesting(true);
    try {
      await api.testNotifChannel(editing.id);
      message.success("Test alert sent — check your endpoint for the payload.");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Test failed");
    } finally {
      setTesting(false);
    }
  }

  async function handleTestResolve() {
    if (!editing) return;
    setTestingResolve(true);
    try {
      await api.testNotifChannel(editing.id, "resolve");
      message.success("Test resolve sent — check your endpoint for the payload.");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Test resolve failed");
    } finally {
      setTestingResolve(false);
    }
  }

  useEffect(() => {
    if (open) {
      if (editing) {
        form.setFieldsValue({
          name: editing.name,
          type: editing.type,
          enabled: editing.enabled,
          bot_token: editing.config.bot_token ?? "",
          chat_id: editing.config.chat_id ?? "",
          api_base: editing.config.api_base ?? "",
          url: editing.config.url ?? "",
          alert_template: editing.config.alert_template ?? "",
          resolve_template: editing.config.resolve_template ?? "",
        });
        setChType(editing.type);
      } else if (cloneFrom) {
        // Clone mode: pre-fill with source channel config, new name, no ID.
        form.setFieldsValue({
          name: `Copy of ${cloneFrom.name}`,
          type: cloneFrom.type,
          enabled: cloneFrom.enabled,
          bot_token: cloneFrom.config.bot_token ?? "",
          chat_id: cloneFrom.config.chat_id ?? "",
          api_base: cloneFrom.config.api_base ?? "",
          url: cloneFrom.config.url ?? "",
          alert_template: cloneFrom.config.alert_template ?? "",
          resolve_template: cloneFrom.config.resolve_template ?? "",
        });
        setChType(cloneFrom.type);
      } else {
        form.resetFields();
        form.setFieldValue("type", "telegram");
        form.setFieldValue("enabled", true);
        setChType("telegram");
      }
    }
  }, [open, editing, cloneFrom, form]);

  async function handleOk() {
    const vals = await form.validateFields();
    setSaving(true);
    try {
      const config: Record<string, string> =
        vals.type === "telegram"
          ? {
              bot_token: vals.bot_token ?? "",
              chat_id: vals.chat_id ?? "",
              // Optional custom Bot API base (self-hosted server / proxy).
              // Empty → backend falls back to https://api.telegram.org.
              api_base: (vals.api_base ?? "").trim(),
            }
          : {
              url: vals.url ?? "",
              alert_template: vals.alert_template ?? "",
              resolve_template: vals.resolve_template ?? "",
            };

      const body = {
        name: vals.name,
        type: vals.type,
        config,
        enabled: vals.enabled ?? true,
      };
      if (editing) {
        await api.updateNotifChannel(editing.id, body);
        message.success("Channel updated");
      } else {
        await api.createNotifChannel(body);
        message.success("Channel created");
      }
      onSaved();
      onClose();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={editing ? "Edit Channel" : "New Notification Channel"}
      open={open}
      onCancel={onClose}
      width={500}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Space>
            {editing && (
              <Button icon={<SendOutlined />} loading={testing} onClick={handleTest}>
                {chType === "webhook" ? "Test Alert" : "Send Test"}
              </Button>
            )}
            {editing && chType === "webhook" && (
              <Button icon={<CheckCircleOutlined />} loading={testingResolve} onClick={handleTestResolve}>
                Test Resolve
              </Button>
            )}
          </Space>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleOk}>
              {editing ? "Save" : "Create"}
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical" size="small">
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="e.g. My Telegram Bot" />
        </Form.Item>
        <Form.Item name="type" label="Type" rules={[{ required: true }]}>
          <Select
            disabled={!!editing}
            options={[
              { value: "telegram", label: "Telegram" },
              { value: "webhook", label: "Webhook (HTTP POST)" },
            ]}
            onChange={(v) => setChType(v)}
          />
        </Form.Item>

        {chType === "telegram" && (
          <>
            <Form.Item
              name="bot_token"
              label="Bot Token"
              rules={[{ required: !editing }]}
            >
              <Input.Password
                placeholder={editing ? "leave blank to keep existing" : "123456:ABC-DEF..."}
              />
            </Form.Item>
            <Form.Item
              name="chat_id"
              label="Chat ID"
              rules={[{ required: true }]}
            >
              <Input placeholder="-100123456789 or @username" />
            </Form.Item>
            <Form.Item
              name="api_base"
              label="API Base URL (optional)"
              tooltip="Override the Telegram Bot API endpoint — use a self-hosted Bot API server or a proxy/relay when api.telegram.org is blocked. Leave blank to use the default. The /bot<token>/sendMessage path is appended automatically."
              rules={[
                {
                  validator: (_, v) =>
                    !v || /^https?:\/\/.+/.test(v.trim())
                      ? Promise.resolve()
                      : Promise.reject(new Error("Must start with http:// or https://")),
                },
              ]}
            >
              <Input
                placeholder="https://api.telegram.org (default)"
                style={{ fontFamily: "monospace" }}
              />
            </Form.Item>
          </>
        )}

        {chType === "webhook" && (
          <>
            <Form.Item
              name="url"
              label="Webhook URL"
              rules={[{ required: true, type: "url" }]}
            >
              <Input placeholder="https://hooks.example.com/alert" />
            </Form.Item>
            <Form.Item
              name="alert_template"
              label="Alert Template"
              rules={[{ required: true, message: "Alert template is required" }]}
              tooltip="Headers and body separated by a blank line. Template variables can be used anywhere."
            >
              <Input.TextArea
                rows={6}
                placeholder={
                  "Content-Type: application/json\n" +
                  "X-API-Key: your-secret-key\n" +
                  "\n" +
                  '{"action": "null", "ip": "{{.TopIP}}"}'
                }
                style={{ fontFamily: "monospace", fontSize: 12 }}
              />
            </Form.Item>
            <Form.Item
              name="resolve_template"
              label="Resolve Template"
              tooltip="Leave blank to skip resolved notifications."
            >
              <Input.TextArea
                rows={6}
                placeholder={
                  "Content-Type: application/json\n" +
                  "X-API-Key: your-secret-key\n" +
                  "\n" +
                  '{"action": "unnull", "ip": "{{.TopIP}}"}'
                }
                style={{ fontFamily: "monospace", fontSize: 12 }}
              />
            </Form.Item>
            <Collapse
              ghost
              size="small"
              style={{ marginBottom: 12 }}
              items={[{
                key: "vars",
                label: <span style={{ fontSize: 12, color: "var(--muted)" }}>📖 Available template variables</span>,
                children: (
                  <table style={{ fontSize: 11, borderCollapse: "collapse", width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: "2px 8px", fontFamily: "monospace" }}>Variable</th>
                        <th style={{ textAlign: "left", padding: "2px 8px" }}>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {([
                        ["{{.Event}}",          "firing | resolved | test"],
                        ["{{.Rule}}",           "Rule name"],
                        ["{{.Metric}}",         "bps_in / bps_out / pps_in / pps_out"],
                        ["{{.Value}}",          "Formatted value (e.g. 1.2 Gbps)"],
                        ["{{.Threshold}}",      "Formatted threshold"],
                        ["{{.Target}}",         "global | subnet CIDR | Per-IP | breaching subnet CIDR (per-subnet)"],
                        ["{{.TopIP}}",          "Top offender — IP (per-IP rules) or subnet CIDR (per-subnet rules)"],
                        ["{{.Subnet}}",         "Breaching subnet CIDR, e.g. \"103.37.1.0/24\" (per-subnet rules only)"],
                        ["{{.IPCount}}",        "Number of offenders firing — IPs (per-IP) or subnets (per-subnet)"],
                        ["{{.Time}}",           "Timestamp"],
                        ["{{.Duration}}",       "How long above threshold (resolved only)"],
                        ["{{.Decoder}}",        "Protocol filter name (e.g. \"TCP+SYN\"); empty for aggregate rules"],
                        ["{{.TopTalkers}}",     "Pre-formatted top-5 offending IPs (multi-line)"],
                        ["{{.TopProtos}}",      "Pre-formatted top-5 protocols (multi-line)"],
                        ["{{.TopTalker}}",      "Top offender IP only (single value)"],
                        ["{{.TopTalkerValue}}", "Top offender's formatted value (e.g. \"450 Mbps\")"],
                        ["{{.TopTalkerPct}}",   "Top offender's % share of metric"],
                        ["{{.TopProto}}",       "Top protocol name only (e.g. \"UDP\")"],
                        ["{{.TopProtoValue}}",  "Top protocol's formatted value"],
                        ["{{.TopProtoPct}}",    "Top protocol's % share"],
                        ["{{.IPZone}}",         "IP zone name of the trigger IP / subnet (e.g. \"VN-Core\")"],
                        ["{{.IPGroup}}",        "IP group label of the trigger IP / subnet (e.g. \"MegaHost\"); empty if unset"],
                      ] as [string, string][]).map(([v, d]) => (
                        <tr key={v}>
                          <td style={{ padding: "2px 8px", fontFamily: "monospace", color: "var(--cyan)" }}>{v}</td>
                          <td style={{ padding: "2px 8px", color: "var(--muted)" }}>{d}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ),
              }]}
            />
          </>
        )}

        <Form.Item name="enabled" label="Enabled" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── RuleModal ──────────────────────────────────────────────────────────────

interface RuleModalProps {
  open: boolean;
  editing: AlertRule | null;
  channels: NotifChannel[];
  onClose: () => void;
  onSaved: () => void;
}
function RuleModal({ open, editing, channels, onClose, onSaved }: RuleModalProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [targetType, setTargetType] = useState<"global" | "subnet" | "ip" | "zone_subnet">("global");
  const [metric, setMetric] = useState<AlertMetric>("bps_in");
  const [threshold, setThreshold] = useState(0);
  const [geoClass, setGeoClass] = useState<string>("");
  // decoderID: 0 = aggregate (legacy); non-zero = filter rule to flows matching
  // this decoder, e.g. "TCP+SYN" for SYN-flood detection.
  const [decoderID, setDecoderID] = useState<number>(0);
  const [decoderOptions, setDecoderOptions] = useState<Array<{ value: number; label: string }>>([]);
  // Template preview drawer state. Kept in the modal scope so its current
  // template text is fed into the preview without going through the
  // form (which would force a validate cycle).
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedChannelType, setSelectedChannelType] = useState<"telegram" | "webhook" | "">("");
  const { zones } = useIPZone();
  const { settings: geoSettings } = useGeoSettings();

  // Lazy-load decoder list when modal opens. Only "enabled" decoders show
  // up — alerting on a decoder the sensor doesn't track would always read 0.
  useEffect(() => {
    if (!open) return;
    api.decoders().then((list) => {
      const opts = list
        .filter((d) => d.enabled)
        .map((d) => ({ value: d.id, label: d.name }));
      setDecoderOptions([{ value: 0, label: "All protocols (aggregate)" }, ...opts]);
    }).catch(() => {/* leave dropdown showing only the All option */});
  }, [open]);

  // Whether at least one geo class option is available.
  const anyGeoEnabled = geoSettings.ixp_enabled || geoSettings.nix_enabled;

  useEffect(() => {
    if (open) {
      if (editing) {
        setTargetType(editing.target_type);
        setMetric(editing.metric);
        setThreshold(editing.threshold);
        setGeoClass(editing.geo_class ?? "");
        setDecoderID(editing.decoder_id ?? 0);
        {
          const ids = editing.channel_ids ?? (editing.channel_id ? [editing.channel_id] : []);
          const sel = channels.filter((c) => ids.includes(c.id));
          setSelectedChannelType(
            sel.some((c) => c.type === "webhook") ? "webhook"
              : sel.some((c) => c.type === "telegram") ? "telegram" : ""
          );
        }
        form.setFieldsValue({
          name: editing.name,
          enabled: editing.enabled,
          target_type: editing.target_type,
          target_value: editing.target_value,
          metric: editing.metric,
          sustained_s: editing.sustained_s,
          cooldown_s: editing.cooldown_s,
          notify_resolve: editing.notify_resolve,
          resolve_delay_s: editing.resolve_delay_s ?? 0,
          exclude_google: editing.exclude_google ?? false,
          exclude_cloudflare: editing.exclude_cloudflare ?? false,
          channel_ids: editing.channel_ids ?? (editing.channel_id ? [editing.channel_id] : []),
          message_template: editing.message_template ?? "",
          zone_filter_ids: editing.zone_filter_ids ?? [],
          // Active-window: parse "HH:MM" strings into dayjs for TimePicker.
          // null = disabled (always active).
          active_window:
            editing.active_window_start && editing.active_window_end
              ? [
                  dayjs(editing.active_window_start, "HH:mm"),
                  dayjs(editing.active_window_end, "HH:mm"),
                ]
              : null,
          active_window_tz: editing.active_window_tz || "Asia/Ho_Chi_Minh",
        });
      } else {
        setTargetType("global");
        setMetric("bps_in");
        setThreshold(0);
        setGeoClass("");
        setDecoderID(0);
        setSelectedChannelType("");
        form.resetFields();
        form.setFieldsValue({
          enabled: true,
          target_type: "global",
          target_value: "",
          metric: "bps_in",
          sustained_s: 60,
          cooldown_s: 300,
          notify_resolve: true,
          resolve_delay_s: 0,
          exclude_google: false,
          exclude_cloudflare: false,
          message_template: "",
          zone_filter_ids: [],
          active_window: null,
          active_window_tz: "Asia/Ho_Chi_Minh",
        });
      }
    }
  }, [open, editing, form]);

  async function handleOk() {
    const vals = await form.validateFields();
    setSaving(true);
    try {
      const zoneIDs = (vals.target_type === "ip" || vals.target_type === "zone_subnet")
        ? ((vals.zone_filter_ids as number[]) || []).filter((id) => id > 0)
        : [];
      // Active-window: TimePicker.RangePicker emits [Dayjs, Dayjs] | null.
      // Convert to "HH:MM" strings the backend stores; null → empty strings
      // = window disabled = rule always active.
      const aw = vals.active_window as [Dayjs, Dayjs] | null | undefined;
      const awStart = aw && aw[0] ? aw[0].format("HH:mm") : "";
      const awEnd   = aw && aw[1] ? aw[1].format("HH:mm") : "";
      const body = {
        name: vals.name,
        enabled: vals.enabled ?? true,
        target_type: vals.target_type,
        target_value: vals.target_type === "subnet" ? (vals.target_value ?? "") : "",
        metric: vals.metric,
        threshold,
        sustained_s: vals.sustained_s ?? 60,
        cooldown_s: vals.cooldown_s ?? 300,
        notify_resolve: vals.notify_resolve ?? true,
        resolve_delay_s: (selectedChannelType === "webhook" && vals.notify_resolve !== false)
          ? (vals.resolve_delay_s ?? 0)
          : 0,
        channel_ids: (vals.channel_ids as number[]) ?? [],
        message_template: vals.message_template ?? "",
        zone_filter_ids: zoneIDs,
        // Per-protocol filter: 0 = aggregate (NULL on the wire); non-zero
        // = decoder ID. When set, the engine ignores geo_class because the
        // proto store is geo-agnostic — make that explicit here too so the
        // saved rule reads cleanly.
        decoder_id: decoderID || null,
        // Traffic Source (geo class) is honoured as selected — including
        // alongside a protocol filter. Since v1.5.0 the engine evaluates
        // combined "IXP + TCP" rules against the per-class proto store
        // (live_proto_top_{ixp,nix}.json). The checkboxes only expose
        // currently-enabled classes, so geoClass is already valid.
        geo_class: geoClass,
        // CDN exclusion only applies to per-IP rules; force off for
        // global/subnet so a stale checkbox value doesn't survive a
        // target_type change.
        exclude_google:     vals.target_type === "ip" ? !!vals.exclude_google     : false,
        exclude_cloudflare: vals.target_type === "ip" ? !!vals.exclude_cloudflare : false,
        active_window_start: awStart,
        active_window_end:   awEnd,
        active_window_tz:    vals.active_window_tz || "Asia/Ho_Chi_Minh",
      };
      if (editing) {
        await api.updateNotifRule(editing.id, body);
        message.success("Rule updated");
      } else {
        await api.createNotifRule(body);
        message.success("Rule created");
      }
      onSaved();
      onClose();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={editing ? "Edit Alert Rule" : "New Alert Rule"}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={saving}
      okText={editing ? "Save" : "Create"}
      width={560}
    >
      <Form form={form} layout="vertical" size="small">
        <Form.Item name="name" label="Rule Name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Global Inbound > 1 Gbps" />
        </Form.Item>

        <Form.Item name="target_type" label="Target" rules={[{ required: true }]}>
          <Select
            options={[
              { value: "global",      label: "🌐 Global (all traffic)" },
              { value: "subnet",      label: "🗂 Subnet / CIDR" },
              { value: "ip",          label: "📍 Per-IP (any single IP)" },
              { value: "zone_subnet", label: "🧩 Per-subnet (any subnet in IP zone)" },
            ]}
            onChange={(v) => setTargetType(v)}
          />
        </Form.Item>

        {targetType === "subnet" && (
          <Form.Item
            name="target_value"
            label="CIDR"
            rules={[
              { required: true },
              {
                pattern: /^[\d.:a-fA-F]+\/\d+$/,
                message: "Enter a valid CIDR, e.g. 10.0.0.0/8",
              },
            ]}
          >
            <Input placeholder="10.0.0.0/8" style={{ fontFamily: "monospace" }} />
          </Form.Item>
        )}

        {targetType === "ip" && (
          <>
            <div style={{
              padding: "8px 12px",
              background: "rgba(246,176,0,0.07)",
              border: "1px solid rgba(246,176,0,0.28)",
              borderRadius: 6,
              fontSize: 12,
              color: "var(--amber)",
              marginBottom: 12,
            }}>
              📡 Engine scans <b>all IPs</b> in live flow every 5s.
              Alert fires when <b>any single IP</b> exceeds the threshold — the culprit IP is named in the notification.
            </div>
            <Form.Item
              name="zone_filter_ids"
              label="Zone filter"
              tooltip="Limit alerting to IPs that belong to specific IP Zones. Leave empty to monitor every IP in live traffic. Select multiple zones to alert when any IP in any of those zones breaches the threshold."
            >
              <Select
                mode="multiple"
                placeholder="All zones (monitor every IP)"
                allowClear
                options={zones.map((z) => ({
                  value: z.id,
                  label: (
                    <span>
                      🗂 <b>{z.name}</b>
                      {z.has_ipv6 && <Tag style={{ marginLeft: 6, fontSize: 10 }}>v6</Tag>}
                    </span>
                  ),
                }))}
              />
            </Form.Item>
          </>
        )}

        {targetType === "zone_subnet" && (
          <>
            <div style={{
              padding: "8px 12px",
              background: "rgba(56,189,248,0.07)",
              border: "1px solid rgba(56,189,248,0.28)",
              borderRadius: 6,
              fontSize: 12,
              color: "var(--cyan)",
              marginBottom: 12,
            }}>
              🧩 Engine sums each <b>declared subnet</b> in the chosen zone(s) every 5s.
              Alert fires when <b>any subnet</b> exceeds the threshold — the breaching
              CIDR (e.g. <code>103.130.216.0/23</code>) is named in the notification.
            </div>
            <Form.Item
              name="zone_filter_ids"
              label="IP Zone(s)"
              tooltip="The declared subnets in these IP Zones become the alert targets — each is evaluated independently. Leave empty to watch every subnet across all zones."
            >
              <Select
                mode="multiple"
                placeholder="All zones (every declared subnet)"
                allowClear
                options={zones.map((z) => ({
                  value: z.id,
                  label: (
                    <span>
                      🗂 <b>{z.name}</b>
                      {z.has_ipv6 && <Tag style={{ marginLeft: 6, fontSize: 10 }}>v6</Tag>}
                    </span>
                  ),
                }))}
              />
            </Form.Item>
          </>
        )}

        <Form.Item name="metric" label="Metric" rules={[{ required: true }]}>
          <Select
            options={[
              { value: "bps_in",  label: "Inbound bps" },
              { value: "bps_out", label: "Outbound bps" },
              { value: "pps_in",  label: "Inbound pps" },
              { value: "pps_out", label: "Outbound pps" },
            ]}
            onChange={(v) => {
              setMetric(v);
              // reset threshold when switching metric family
              setThreshold(0);
            }}
          />
        </Form.Item>

        {/* Geo class filter — only visible when at least one geo feature is enabled */}
        {anyGeoEnabled && (
          <Form.Item
            label="Traffic Source"
            tooltip="Filter this rule to a specific traffic class. Leave unselected to alert on total (combined) traffic."
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <span style={{ fontSize: 12, color: "var(--muted)" }}>
                {geoClass === "" ? "Total (all traffic)" : geoClass === "ixp" ? "IXP — International only" : "NIX — Domestic only"}
              </span>
              <Space size={8}>
                {geoSettings.ixp_enabled && (
                  <Checkbox
                    checked={geoClass === "ixp"}
                    onChange={(e) => setGeoClass(e.target.checked ? "ixp" : "")}
                  >
                    <span style={{ fontSize: 12 }}>🌏 IXP <span style={{ color: "var(--muted)", fontSize: 11 }}>(International)</span></span>
                  </Checkbox>
                )}
                {geoSettings.nix_enabled && (
                  <Checkbox
                    checked={geoClass === "nix"}
                    onChange={(e) => setGeoClass(e.target.checked ? "nix" : "")}
                  >
                    <span style={{ fontSize: 12 }}>🇻🇳 NIX <span style={{ color: "var(--muted)", fontSize: 11 }}>(Domestic)</span></span>
                  </Checkbox>
                )}
              </Space>
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
              Leave both unchecked to alert on total combined traffic.
            </div>
          </Form.Item>
        )}

        <Form.Item
          label="Protocol filter (optional)"
          tooltip="Restrict this rule to flows matching a specific decoder. Useful for DDoS detection: TCP+SYN catches SYN-floods, UDP catches amplification, ICMP catches ping floods. Can be combined with a Geo class (e.g. IXP + UDP) — the engine reads the per-class proto store."
        >
          <Select
            value={decoderID}
            onChange={(v) => setDecoderID(v as number)}
            options={decoderOptions}
            style={{ minWidth: 280 }}
          />
        </Form.Item>

        <Form.Item label="Threshold (fires when exceeded)">
          <ThresholdInput
            metric={metric}
            value={threshold}
            onChange={setThreshold}
          />
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
            Preview: <b>{fmtValue(metric, threshold)}</b>
          </div>
        </Form.Item>

        <Form.Item
          name="sustained_s"
          label="Sustained (seconds)"
          tooltip="Must exceed threshold continuously for this many seconds before firing. Prevents flash-spike false alerts."
          rules={[{ required: true }]}
        >
          <SecondsInput />
        </Form.Item>

        <Form.Item
          name="cooldown_s"
          label="Cooldown (seconds)"
          tooltip="After firing, suppress further alerts for this many seconds."
          rules={[{ required: true }]}
        >
          <SecondsInput />
        </Form.Item>

        {/* ── Active-window ─────────────────────────────────────────────── */}
        <Form.Item
          name="active_window"
          label="Active Window (optional)"
          tooltip="Rule only evaluates within this time window. Leave empty for 24/7. If the end time is earlier than the start, the window spans midnight (e.g. 22:00–06:00 = 10pm to 6am)."
        >
          <TimePicker.RangePicker
            format="HH:mm"
            minuteStep={5}
            placeholder={["From", "To"]}
            allowEmpty={[true, true]}
            order={false}
          />
        </Form.Item>

        <Form.Item
          name="active_window_tz"
          label="Timezone"
          tooltip="Timezone used for the Active Window. Defaults to Asia/Ho_Chi_Minh."
        >
          <Select
            showSearch
            options={[
              { value: "Asia/Ho_Chi_Minh", label: "Asia/Ho_Chi_Minh (ICT, UTC+7)" },
              { value: "Asia/Bangkok",     label: "Asia/Bangkok (UTC+7)" },
              { value: "Asia/Singapore",   label: "Asia/Singapore (UTC+8)" },
              { value: "Asia/Tokyo",       label: "Asia/Tokyo (UTC+9)" },
              { value: "UTC",              label: "UTC" },
              { value: "Europe/London",    label: "Europe/London" },
              { value: "America/New_York", label: "America/New_York" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="notify_resolve"
          label="Send RESOLVED notification"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        {/* Resolve delay — only meaningful for webhook channels */}
        {selectedChannelType === "webhook" && (
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.notify_resolve !== cur.notify_resolve}
          >
            {({ getFieldValue }) =>
              getFieldValue("notify_resolve") ? (
                <Form.Item
                  name="resolve_delay_s"
                  label="Resolve Delay (seconds)"
                  tooltip="Extra time to wait before sending the RESOLVED webhook after the condition clears. 0 = send immediately (default). Useful to avoid noisy RESOLVED messages on flapping metrics."
                >
                  <SecondsInput />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        )}

        {/* CDN exclusion — per-IP rules only */}
        {targetType === "ip" && (
          <Form.Item
            label="Loại trừ CDN khỏi metric"
            tooltip="Chỉ áp dụng cho rule per-IP. Khi bật, traffic UDP/TCP đến/từ CDN tương ứng sẽ bị trừ ra trước khi so với threshold — tránh false-positive với QUIC/HTTP3 (YouTube, Drive, các site dùng Cloudflare). Tick từng provider riêng để có quyền chọn linh hoạt."
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Form.Item name="exclude_google" valuePropName="checked" noStyle>
                <Checkbox>
                  Google <span style={{ color: "var(--muted)", fontSize: 12 }}>(YouTube, Drive, Gmail, GCP — QUIC/HTTP3)</span>
                </Checkbox>
              </Form.Item>
              <Form.Item name="exclude_cloudflare" valuePropName="checked" noStyle>
                <Checkbox>
                  Cloudflare <span style={{ color: "var(--muted)", fontSize: 12 }}>(websites dùng CF, WARP, Workers)</span>
                </Checkbox>
              </Form.Item>
            </div>
          </Form.Item>
        )}

        <Form.Item
          name="channel_ids"
          label="Notification Channels"
          rules={[{ required: true, type: "array", min: 1, message: "Chọn ít nhất một kênh" }]}
          tooltip="Chọn một hoặc nhiều kênh. Khi rule kích hoạt, thông báo sẽ gửi tới tất cả kênh đã chọn cùng lúc."
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="Select one or more channels"
            optionFilterProp="label"
            onChange={(ids: number[]) => {
              const sel = channels.filter((c) => ids.includes(c.id));
              setSelectedChannelType(
                sel.some((c) => c.type === "webhook") ? "webhook"
                  : sel.some((c) => c.type === "telegram") ? "telegram" : ""
              );
            }}
            options={channels.map((c) => ({
              value: c.id,
              label: `${c.name} (${c.type})${c.enabled ? "" : " — disabled"}`,
            }))}
          />
        </Form.Item>

        {selectedChannelType === "webhook" ? (
          <Form.Item
            name="message_template"
            label="Body Override (optional)"
            tooltip="Leave blank to use the body from the channel's Alert Template. Set here to customise the request body for this rule only — the channel's headers (Content-Type, auth) are kept."
            extra={
              <span style={{ fontSize: 11, color: "var(--muted)" }}>
                Leave blank → uses the body from <b>Channel → Alert Template</b>.
                Set here to override the body <i>for this rule only</i>.
                Headers (Content-Type, X-API-Key, …) always come from the channel.
              </span>
            }
          >
            <Input.TextArea
              rows={3}
              placeholder={'{"action": "null", "ip": "{{.TopIP}}", "rule": "{{.Rule}}"}'}
              style={{ fontFamily: "monospace", fontSize: 12 }}
            />
          </Form.Item>
        ) : (
          <Form.Item
            name="message_template"
            label={
              <span>
                Custom Message Template (optional){" "}
                <Button
                  size="small"
                  type="link"
                  onClick={() => setPreviewOpen(true)}
                  style={{ padding: "0 4px", fontSize: 11 }}
                >
                  🧪 Preview
                </Button>
              </span>
            }
            tooltip="Go text/template syntax. Leave blank to use the built-in format. Use Preview to test against dummy data before saving."
          >
            <Input.TextArea
              rows={4}
              placeholder={"{{if eq .Event \"FIRING\"}}🔴{{else}}✅{{end}} [{{.Event}}] {{.Rule}}\nMetric: {{.Metric}}  Value: {{.Value}}\nThreshold: {{.Threshold}}\n{{if .TopTalker}}\nTop: {{.TopTalker}} → {{.TopTalkerValue}} ({{.TopTalkerPct}}%){{end}}\n{{if .TopTalkers}}\nTop talkers:\n{{.TopTalkers}}{{end}}\n{{if .TopProtos}}\nTop protocols:\n{{.TopProtos}}{{end}}"}
              style={{ fontFamily: "monospace", fontSize: 12 }}
            />
          </Form.Item>
        )}

        <Collapse
          ghost
          size="small"
          style={{ marginBottom: 12 }}
          items={[{
            key: "vars",
            label: <span style={{ fontSize: 12, color: "var(--muted)" }}>📖 Available template variables</span>,
            children: (
              <table style={{ fontSize: 11, borderCollapse: "collapse", width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "2px 8px", fontFamily: "monospace" }}>Variable</th>
                    <th style={{ textAlign: "left", padding: "2px 8px" }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {([
                    // Core fields — always populated.
                    ["{{.Event}}",          "FIRING | RESOLVED | TEST"],
                    ["{{.Rule}}",           "Rule name"],
                    ["{{.Metric}}",         "Inbound bps / Outbound bps / Inbound pps / Outbound pps"],
                    ["{{.Value}}",          "Formatted value (e.g. 1.2 Gbps)"],
                    ["{{.Threshold}}",      "Formatted threshold"],
                    ["{{.Target}}",         "Global | subnet CIDR | Per-IP"],
                    ["{{.TopIP}}",          "IP that triggered (ip-type rules)"],
                    ["{{.IPCount}}",        "Number of IPs firing (ip-type rules)"],
                    ["{{.Time}}",           "Timestamp"],
                    ["{{.Duration}}",       "How long above threshold (resolved only)"],
                    // Forensics fields — populated when the engine captured a
                    // snapshot at fire/resolve time. Empty for older alerts;
                    // gate references with {{if .TopTalker}} etc.
                    ["{{.Decoder}}",        "Protocol filter name (e.g. \"TCP+SYN\"); empty for aggregate rules"],
                    ["{{.TopTalker}}",      "Top offender IP only (single value)"],
                    ["{{.TopTalkerValue}}", "Top offender's formatted value (e.g. \"450 Mbps\")"],
                    ["{{.TopTalkerPct}}",   "Top offender's % share of metric"],
                    ["{{.TopProto}}",       "Top protocol name only (e.g. \"UDP\")"],
                    ["{{.TopProtoValue}}",  "Top protocol's formatted value"],
                    ["{{.TopProtoPct}}",    "Top protocol's % share"],
                    ["{{.TopTalkers}}",     "Pre-formatted top-5 offending IPs (multi-line)"],
                    ["{{.TopProtos}}",      "Pre-formatted top-5 protocols (multi-line)"],
                    ["{{.IPZone}}",         "IP zone name of trigger IP (e.g. \"VN-Core\")"],
                    ["{{.IPGroup}}",        "IP group label of trigger IP (e.g. \"MegaHost\"); empty if unset"],
                  ] as [string, string][]).map(([v, d]) => (
                    <tr key={v}>
                      <td style={{ padding: "2px 8px", fontFamily: "monospace", color: "var(--cyan)" }}>{v}</td>
                      <td style={{ padding: "2px 8px", color: "var(--muted)" }}>{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ),
          }]}
        />

        <Form.Item name="enabled" label="Enabled" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
      <TemplatePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        getTemplate={() => form.getFieldValue("message_template") ?? ""}
      />
    </Modal>
  );
}

// ── TemplatePreviewModal ───────────────────────────────────────────────────
// Renders the current template against synthetic dummy data. Lets the
// operator iterate on a template without waiting for a real alert.
//
// The scenario picker matches the backend's preset payloads (firing,
// resolved, syn-flood, batch, no-forensics) so every {{if}} branch can be
// exercised. Output is plain text; the parent's form-state is untouched.
interface TemplatePreviewModalProps {
  open: boolean;
  onClose: () => void;
  /** Lazy getter so we read the LATEST textarea value at preview time, not
   *  whatever was set when this component mounted. Avoids stale-template
   *  surprises where the user types something and immediately clicks
   *  Preview. */
  getTemplate: () => string;
}
const SCENARIOS: Array<{ value: string; label: string; hint: string }> = [
  { value: "firing",        label: "🔴 Firing — global aggregate",     hint: "target=global, no decoder filter, full forensics" },
  { value: "resolved",      label: "✅ Resolved — same rule, traffic dropped", hint: "Event=RESOLVED, Duration set, smaller forensics" },
  { value: "syn-flood",     label: "⚡ Per-protocol — TCP+SYN flood",   hint: "decoder=TCP+SYN, Per-IP rule, Decoder field set" },
  { value: "batch",         label: "📍 Per-IP — multiple offenders",   hint: "IPCount=5, batch firing" },
  { value: "no-forensics",  label: "(no forensics — pre-v1.4 alert)",  hint: "All forensics fields empty; verify your {{if}} guards" },
];
function TemplatePreviewModal({ open, onClose, getTemplate }: TemplatePreviewModalProps) {
  const [scenario, setScenario] = useState("firing");
  const [rendered, setRendered] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const runPreview = useCallback(async (scn: string) => {
    const tpl = getTemplate();
    if (!tpl.trim()) {
      setError("Template is empty — fill in the textarea first.");
      setRendered("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.previewNotifTemplate(tpl, scn);
      setRendered(res.rendered);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Preview failed");
      setRendered("");
    } finally {
      setLoading(false);
    }
  }, [getTemplate]);

  // Auto-run when the modal opens or the scenario changes.
  useEffect(() => {
    if (open) runPreview(scenario);
  }, [open, scenario, runPreview]);

  return (
    <Modal
      title="🧪 Template Preview"
      open={open}
      onCancel={onClose}
      onOk={onClose}
      okText="Close"
      cancelButtonProps={{ style: { display: "none" } }}
      width={720}
      destroyOnClose
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Scenario</div>
        <Select
          value={scenario}
          onChange={setScenario}
          style={{ width: "100%" }}
          options={SCENARIOS.map((s) => ({ value: s.value, label: s.label }))}
        />
        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
          {SCENARIOS.find((s) => s.value === scenario)?.hint}
        </div>
      </div>

      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>Rendered output</div>
      <div
        style={{
          fontFamily: "Menlo, Monaco, monospace",
          fontSize: 12,
          background: "var(--bg-0)",
          color: "var(--text)",
          padding: 12,
          borderRadius: 4,
          whiteSpace: "pre-wrap",
          minHeight: 200,
          maxHeight: 500,
          overflow: "auto",
          border: error ? "1px solid var(--red)" : "1px solid var(--line-2)",
        }}
      >
        {loading
          ? <span style={{ color: "var(--muted)" }}>Rendering…</span>
          : error
          ? <span style={{ color: "var(--red)" }}>{error}</span>
          : rendered || <span style={{ color: "var(--muted)" }}>(no output)</span>}
      </div>

      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 8 }}>
        💡 Tip: Telegram renders <code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>, <code>&lt;code&gt;</code>, <code>&lt;pre&gt;</code> as markup.
        Other tags appear verbatim. Switch scenarios to verify your <code>{"{{if}}"}</code> guards.
      </div>
    </Modal>
  );
}

// ── ChannelsTab ────────────────────────────────────────────────────────────

interface ChannelsTabProps {
  onChannelsChange: (ch: NotifChannel[]) => void;
}
function ChannelsTab({ onChannelsChange }: ChannelsTabProps) {
  const [channels, setChannels] = useState<NotifChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<NotifChannel | null>(null);
  const [cloning, setCloning] = useState<NotifChannel | null>(null);
  const { isOperator, isAdmin } = useRole();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.notifChannels();
      setChannels(data);
      onChannelsChange(data);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Failed to load channels");
    } finally {
      setLoading(false);
    }
  }, [onChannelsChange]);

  useEffect(() => { load(); }, [load]);

  async function handleTest(id: number) {
    setTesting(id);
    try {
      await api.testNotifChannel(id);
      message.success("Test message sent!");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Test failed");
    } finally {
      setTesting(null);
    }
  }

  async function handleDelete(id: number) {
    try {
      await api.deleteNotifChannel(id);
      message.success("Channel deleted");
      load();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Delete failed");
    }
  }

  function handleClone(r: NotifChannel) {
    setEditing(null);
    setCloning(r);
    setModalOpen(true);
  }

  const columns: ColumnsType<NotifChannel> = [
    { title: "ID", dataIndex: "id", width: 60 },
    { title: "Name", dataIndex: "name", render: (v, r) => (
      <Space>
        {r.type === "telegram" ? "📨" : "🔗"}
        <b>{v}</b>
      </Space>
    )},
    { title: "Type", dataIndex: "type", width: 100, render: (v) => <Tag>{v}</Tag> },
    { title: "Config", dataIndex: "config", render: (cfg: Record<string, string>, r) =>
        r.type === "telegram"
          ? <span style={{ fontFamily: "monospace", fontSize: 11 }}>chat_id: {cfg.chat_id}</span>
          : <span style={{ fontFamily: "monospace", fontSize: 11 }}>{cfg.url}</span>
    },
    { title: "Enabled", dataIndex: "enabled", width: 80, render: (v) =>
        v ? <Tag color="green">ON</Tag> : <Tag color="default">OFF</Tag>
    },
    {
      title: "Actions",
      width: 210,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Send test message">
            <Button
              size="small"
              icon={<SendOutlined />}
              loading={testing === r.id}
              disabled={!isOperator}
              onClick={() => handleTest(r.id)}
            >
              Test
            </Button>
          </Tooltip>
          {isOperator && (
            <Tooltip title="Clone channel">
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleClone(r)}
              />
            </Tooltip>
          )}
          {isOperator && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => { setEditing(r); setCloning(null); setModalOpen(true); }}
            />
          )}
          {isAdmin && (
            <Popconfirm
              title="Delete this channel?"
              description="Rules using it will still fire but delivery will fail."
              onConfirm={() => handleDelete(r.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {isOperator && (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => { setEditing(null); setModalOpen(true); }}
          >
            Add Channel
          </Button>
        )}
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </div>
      <Table
        size="small"
        dataSource={channels}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
      />
      <ChannelModal
        open={modalOpen}
        editing={editing}
        cloneFrom={cloning}
        onClose={() => { setModalOpen(false); setCloning(null); }}
        onSaved={() => { setCloning(null); load(); }}
      />
    </div>
  );
}

// ── RulesTab ───────────────────────────────────────────────────────────────

function statusBadge(status: string) {
  switch (status) {
    case "firing":  return <Badge status="error"   text={<b style={{ color: "var(--red)" }}>FIRING</b>} />;
    case "pending": return <Badge status="warning" text="PENDING" />;
    default:        return <Badge status="success" text="OK" />;
  }
}

// fmtBpsForensics / fmtPpsForensics — compact display for forensics tables.
// Reuses the same scale thresholds as the dashboard widgets so values
// look consistent ("1.2 Gbps" vs the Telegram message).
function fmtBpsForensics(v: number): string {
  if (v >= 1e9) return (v / 1e9).toFixed(2) + " Gbps";
  if (v >= 1e6) return (v / 1e6).toFixed(1) + " Mbps";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " kbps";
  return Math.round(v) + " bps";
}
function fmtPpsForensics(v: number): string {
  if (v >= 1e6) return (v / 1e6).toFixed(2) + " Mpps";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " kpps";
  return Math.round(v) + " pps";
}

// ForensicsView renders the captured top talkers + protocol breakdown
// in a compact 2-column layout — fits inside an antd Popover without
// scrollbars for the typical 10×6 entry case.
function ForensicsView({ f }: { f: AlertForensics }) {
  const isBPS = f.metric === "bps_in" || f.metric === "bps_out";
  const valFor = (bps: number, pps: number) =>
    isBPS ? fmtBpsForensics(bps) : fmtPpsForensics(pps);
  return (
    <div style={{ minWidth: 380, maxWidth: 520 }}>
      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8 }}>
        <b>{f.metric}</b> hit{" "}
        <span style={{ fontFamily: "monospace", color: "var(--strong)" }}>
          {valFor(f.value, f.value)}
        </span>
        {" "}vs threshold{" "}
        <span style={{ fontFamily: "monospace", color: "var(--red)" }}>
          {valFor(f.threshold, f.threshold)}
        </span>
        {f.decoder && (
          <Tag color="purple" style={{ marginLeft: 6, fontSize: 10 }}>
            {f.decoder}
          </Tag>
        )}
      </div>
      {(f.top_talkers?.length ?? 0) > 0 && (
        <>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Top talkers</div>
          <table style={{ width: "100%", fontSize: 11, fontFamily: "monospace", marginBottom: 12 }}>
            <tbody>
              {f.top_talkers.slice(0, 10).map((t) => (
                <tr key={t.addr}>
                  <td style={{ padding: "1px 4px", color: "var(--muted)", textAlign: "right" }}>
                    {t.pct.toFixed(0)}%
                  </td>
                  <td style={{ padding: "1px 4px" }}>{t.addr}</td>
                  <td style={{ padding: "1px 4px", textAlign: "right" }}>
                    {valFor(t.bps, t.pps)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {(f.top_protos?.length ?? 0) > 0 && (
        <>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Top protocols</div>
          <table style={{ width: "100%", fontSize: 11, fontFamily: "monospace" }}>
            <tbody>
              {f.top_protos.slice(0, 6).map((p) => (
                <tr key={p.decoder}>
                  <td style={{ padding: "1px 4px", color: "var(--muted)", textAlign: "right" }}>
                    {p.pct.toFixed(0)}%
                  </td>
                  <td style={{ padding: "1px 4px" }}>{p.decoder}</td>
                  <td style={{ padding: "1px 4px", textAlign: "right" }}>
                    {valFor(p.bps, p.pps)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

interface RulesTabProps {
  channels: NotifChannel[];
}
function RulesTab({ channels }: RulesTabProps) {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AlertRule | null>(null);
  const [resolving, setResolving] = useState<number | null>(null);
  const { isOperator, isAdmin } = useRole();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await api.notifRules());
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Failed to load rules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id: number) {
    try {
      await api.deleteNotifRule(id);
      message.success("Rule deleted");
      load();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Delete failed");
    }
  }

  async function handleResolve(id: number) {
    setResolving(id);
    try {
      const res = await api.resolveNotifRule(id);
      message.success(res.was_firing
        ? "Resolved — RESOLVED sent to channels"
        : "Rule was not firing");
      load();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Resolve failed");
    } finally {
      setResolving(null);
    }
  }

  const columns: ColumnsType<AlertRule> = [
    { title: "Status", dataIndex: "status", width: 110, render: statusBadge },
    { title: "Name", dataIndex: "name", render: (v, r) => (
      <Space direction="vertical" size={0}>
        <b>{v}</b>
        {!r.enabled && <Tag color="default" style={{ fontSize: 10 }}>disabled</Tag>}
      </Space>
    )},
    { title: "Target", width: 220, render: (_, r) => {
        const geoTag = r.geo_class === "ixp"
          ? <Tag color="geekblue" style={{ fontSize: 10 }}>🌏 IXP</Tag>
          : r.geo_class === "nix"
          ? <Tag color="cyan" style={{ fontSize: 10 }}>🇻🇳 NIX</Tag>
          : null;
        const protoTag = r.decoder_id
          ? <Tag color="purple" style={{ fontSize: 10 }}>{r.decoder_name || `dec#${r.decoder_id}`}</Tag>
          : null;
        if (r.target_type === "global")
          return <Space size={4} wrap><Tag icon={<span style={{ marginRight: 3 }}>🌐</span>}>Global</Tag>{geoTag}{protoTag}</Space>;
        if (r.target_type === "subnet")
          return <Space size={4} wrap><Tag color="blue" style={{ fontFamily: "monospace" }}>🗂 {r.target_value}</Tag>{geoTag}{protoTag}</Space>;
        if (r.target_type === "ip")
          return (
            <Space size={4} wrap>
              <Tag color="purple">📍 Per-IP</Tag>
              {r.zone_filter_names && r.zone_filter_names.length > 0
                ? r.zone_filter_names.map((name) => (
                    <Tag key={name} color="orange" style={{ fontSize: 10 }}>🗂 {name}</Tag>
                  ))
                : <Tag style={{ fontSize: 10, color: "var(--muted)" }}>all zones</Tag>}
              {geoTag}
              {protoTag}
            </Space>
          );
        if (r.target_type === "zone_subnet")
          return (
            <Space size={4} wrap>
              <Tag color="cyan">🧩 Per-subnet</Tag>
              {r.zone_filter_names && r.zone_filter_names.length > 0
                ? r.zone_filter_names.map((name) => (
                    <Tag key={name} color="orange" style={{ fontSize: 10 }}>🗂 {name}</Tag>
                  ))
                : <Tag style={{ fontSize: 10, color: "var(--muted)" }}>all zones</Tag>}
              {geoTag}
              {protoTag}
            </Space>
          );
        return <Tag>{r.target_value || r.target_type}</Tag>;
    }},
    { title: "Metric", dataIndex: "metric", width: 120, render: metricLabel },
    { title: "Threshold", dataIndex: "threshold", width: 120,
      render: (v, r) => fmtValue(r.metric, v) },
    { title: "Sustained", dataIndex: "sustained_s", width: 90,
      render: (v) => `${v}s` },
    { title: "Cooldown", dataIndex: "cooldown_s", width: 90,
      render: (v) => `${v}s` },
    { title: "Resolve delay", dataIndex: "resolve_delay_s", width: 110,
      render: (v, r) => (v > 0 && r.notify_resolve)
        ? <Tooltip title="Extra wait before sending RESOLVED (webhook only)"><Tag color="orange">{v}s</Tag></Tooltip>
        : <span style={{ color: "var(--muted)", fontSize: 11 }}>—</span>
    },
    { title: "Active Window", width: 140, render: (_, r) => (
        r.active_window_start && r.active_window_end ? (
          <Tooltip title={`Timezone: ${r.active_window_tz || "Asia/Ho_Chi_Minh"}`}>
            <Tag color="purple" style={{ fontFamily: "monospace", fontSize: 11 }}>
              🕒 {r.active_window_start}–{r.active_window_end}
            </Tag>
          </Tooltip>
        ) : (
          <Tag style={{ fontSize: 10, color: "var(--muted)" }}>24/7</Tag>
        )
    )},
    { title: "Channels", dataIndex: "channel_names", width: 160,
      render: (_, r) =>
        (r.channel_names && r.channel_names.length > 0)
          ? r.channel_names.map((n) => <Tag key={n} style={{ fontSize: 10 }}>{n}</Tag>)
          : <span style={{ color: "var(--muted)" }}>—</span>
    },
    { title: "Last fired", dataIndex: "last_fired", width: 130,
      render: (v) => v ? new Date(v).toLocaleString() : "—" },
    { title: "Forensics", width: 100,
      render: (_, r) => r.forensics
        ? (
          <Popover
            placement="left"
            title={
              <span>
                Captured at{" "}
                <span style={{ fontFamily: "monospace", fontSize: 11 }}>
                  {new Date(r.forensics.captured_at * 1000).toLocaleString()}
                </span>
              </span>
            }
            content={<ForensicsView f={r.forensics} />}
          >
            <Button size="small" type="link">view</Button>
          </Popover>
        )
        : <span style={{ color: "var(--muted)", fontSize: 11 }}>—</span>
    },
    {
      title: "Actions",
      width: 170,
      render: (_, r) => (
        <Space size={4}>
          {isOperator && r.status === "firing" && (
            <Popconfirm
              title="Resolve this alert now?"
              description="Sends RESOLVED to the rule's channels and clears the firing state."
              onConfirm={() => handleResolve(r.id)}
              okText="Resolve"
            >
              <Button
                size="small"
                type="primary"
                ghost
                icon={<CheckCircleOutlined />}
                loading={resolving === r.id}
              >
                Resolve
              </Button>
            </Popconfirm>
          )}
          {isOperator && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => { setEditing(r); setModalOpen(true); }}
            />
          )}
          {isAdmin && (
            <Popconfirm
              title="Delete this rule?"
              onConfirm={() => handleDelete(r.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {isOperator && (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => { setEditing(null); setModalOpen(true); }}
          >
            Add Rule
          </Button>
        )}
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </div>
      <Table
        size="small"
        dataSource={rules}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1000 }}
      />
      <RuleModal
        open={modalOpen}
        editing={editing}
        channels={channels}
        onClose={() => setModalOpen(false)}
        onSaved={load}
      />
    </div>
  );
}

// ── HistoryTab ─────────────────────────────────────────────────────────────

const { Text } = Typography;

function HistoryTab() {
  const [items, setItems]         = useState<NotifHistoryItem[]>([]);
  const [ruleStatus, setRuleStatus] = useState<Record<number, string>>({});
  const [resolving, setResolving]   = useState<number | null>(null);
  const [loading, setLoading]     = useState(false);
  const [retention, setRetention] = useState<number>(90);
  const [retLoading, setRetLoading] = useState(false);
  const [retSaving, setRetSaving]   = useState(false);
  const [flushing, setFlushing]     = useState(false);
  const { isAdmin, isOperator } = useRole();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch history + current rule states together so Resolve only shows for a
      // FIRING row whose rule is still firing now (a rule that has since
      // recovered must not offer Resolve on its old FIRING entry).
      const [hist, rules] = await Promise.all([
        api.notifHistory({ limit: 200 }),
        api.notifRules().catch(() => [] as AlertRule[]),
      ]);
      setItems(hist);
      const m: Record<number, string> = {};
      for (const r of rules) m[r.id] = r.status;
      setRuleStatus(m);
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, []);

  async function handleResolve(id: number) {
    setResolving(id);
    try {
      const res = await api.resolveNotifRule(id);
      message.success(res.was_firing
        ? "Resolved — RESOLVED sent to channels"
        : "Rule was not firing");
      load();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Resolve failed");
    } finally {
      setResolving(null);
    }
  }

  const loadRetention = useCallback(async () => {
    setRetLoading(true);
    try {
      const r = await api.notifHistoryRetention();
      setRetention(r.days);
    } catch { /* ignore */ } finally {
      setRetLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadRetention(); }, [load, loadRetention]);

  const handleSaveRetention = async () => {
    setRetSaving(true);
    try {
      const r = await api.setNotifHistoryRetention(retention);
      setRetention(r.days);
      message.success("Retention saved");
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setRetSaving(false);
    }
  };

  const handleFlush = async () => {
    setFlushing(true);
    try {
      const res = await api.flushNotifHistory();
      message.success(`Flushed ${res.deleted} history row${res.deleted !== 1 ? "s" : ""}`);
      load();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Flush failed");
    } finally {
      setFlushing(false);
    }
  };

  const columns: ColumnsType<NotifHistoryItem> = [
    { title: "Time", dataIndex: "sent_at", width: 160,
      render: (v) => new Date(v).toLocaleString() },
    { title: "Event", dataIndex: "event", width: 100, render: (v) => {
        if (v === "firing")   return <Tag color="red">🔴 FIRING</Tag>;
        if (v === "resolved") return <Tag color="green">✅ RESOLVED</Tag>;
        return <Tag>TEST</Tag>;
    }},
    { title: "Rule", dataIndex: "rule_name" },
    { title: "Channel", dataIndex: "channel_name", width: 140 },
    { title: "Value", width: 110, render: (_, r) =>
        r.event !== "test" ? fmtBps(r.value) : "—"
    },
    { title: "Status", dataIndex: "ok", width: 80, render: (ok) =>
        ok
          ? <CheckCircleOutlined style={{ color: "var(--green)" }} />
          : <CloseCircleOutlined style={{ color: "var(--red)" }} />
    },
    { title: "Error", dataIndex: "error", render: (e) =>
        e ? <span style={{ color: "var(--red)", fontSize: 11 }}>{e}</span> : null
    },
    { title: "Forensics", width: 90,
      render: (_, r) => r.forensics
        ? (
          <Popover
            placement="left"
            title="Forensics snapshot"
            content={<ForensicsView f={r.forensics} />}
          >
            <Button size="small" type="link">view</Button>
          </Popover>
        )
        : <span style={{ color: "var(--muted)", fontSize: 11 }}>—</span>
    },
    { title: "Actions", width: 120,
      render: (_, r) => {
        // Offer Resolve only on a FIRING row whose rule is still firing now.
        const canResolve = isOperator && r.event === "firing" && ruleStatus[r.rule_id] === "firing";
        if (!canResolve) return <span style={{ color: "var(--muted)", fontSize: 11 }}>—</span>;
        return (
          <Popconfirm
            title="Resolve this alert now?"
            description="Sends RESOLVED to the rule's channels and clears the firing state."
            onConfirm={() => handleResolve(r.rule_id)}
            okText="Resolve"
          >
            <Button
              size="small"
              type="primary"
              ghost
              icon={<CheckCircleOutlined />}
              loading={resolving === r.rule_id}
            >
              Resolve
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div>
      {/* ── Toolbar row ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
        <Text type="secondary" style={{ fontSize: 12 }}>Last 200 entries</Text>
      </div>

      {/* ── Retention + flush row (admin only) ──────────────────────── */}
      {isAdmin && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
          padding: "8px 12px",
          background: "var(--bg-2)", border: "1px solid var(--line)", borderRadius: 6,
          flexWrap: "wrap",
        }}>
          <Text style={{ fontSize: 13, whiteSpace: "nowrap" }}>Audit log retention:</Text>
          <Space.Compact size="small">
            <InputNumber
              min={0}
              max={3650}
              value={retention}
              onChange={(v) => setRetention(v ?? 0)}
              disabled={retLoading}
              style={{ width: 70 }}
            />
            <Button disabled style={{ cursor: "default", color: "var(--muted)", background: "var(--bg-1)" }}>days</Button>
          </Space.Compact>
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>(0 = keep forever)</Text>
          <Button
            size="small"
            icon={<SaveOutlined />}
            loading={retSaving}
            onClick={handleSaveRetention}
          >
            Save
          </Button>
          <Popconfirm
            title="Flush notification history?"
            description="Delete ALL notification history rows. Cannot be undone."
            onConfirm={handleFlush}
            okText="Flush"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" icon={<ClearOutlined />} loading={flushing} danger>
              Flush Now
            </Button>
          </Popconfirm>
        </div>
      )}

      <Table
        size="small"
        dataSource={items}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 1020 }}
      />
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function Notifications() {
  const [channels, setChannels] = useState<NotifChannel[]>([]);

  const tabItems = [
    {
      key: "channels",
      label: <span><WifiOutlined /> Channels</span>,
      children: <ChannelsTab onChannelsChange={setChannels} />,
    },
    {
      key: "rules",
      label: <span><BellOutlined /> Alert Rules</span>,
      children: <RulesTab channels={channels} />,
    },
    {
      key: "history",
      label: <span><HistoryOutlined /> History</span>,
      children: <HistoryTab />,
    },
  ];

  return (
    <div style={{ padding: "0 4px" }}>
      <h2 style={{ marginBottom: 16 }}>🔔 Notifications</h2>
      <Tabs defaultActiveKey="channels" items={tabItems} />
    </div>
  );
}
